// Part-2 reviewer-profile ("evaluate") form — July-8 FR-L3: EVERY proposal goes through this
// form, and it opens immediately after Propose/Claim (the proposal stays `incomplete`, hidden
// from AE QC per FR-K15, until this is submitted). July-7 FR-K17: fields auto-populate from what
// we already know and stay fully editable. Angel: expertise field uses "Because of your expertise
// in…" (not "Sub-discipline"); the invite sentence only appears in the justification preview.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  aeSubdisciplines,
  deriveJustification,
  type PreprintView,
  type ProposalView,
  type SelectedView,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { pubmedSearchUrl } from "../../lib/pubmed.js";
import { Button, Field, Input, Modal, Textarea } from "../../components/ui.js";
import { HighestDegreeField } from "./HighestDegreeField.js";
import { clsx } from "clsx";

/** WO-7/22 Track D [36:45–38:00]: shown under the Best-Match-Paper fields. */
const BMP_NOTE = "Make sure this is not a review — review papers are not accepted.";

export function EvaluateModal({ proposal, onClose }: { proposal: ProposalView; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  // WO-7/22 Track E: the AE-seeded sub-discipline options live on the linked deck's pitch —
  // resolve preprint → selected → word-box so the student can pick one instead of free-filling.
  const { data: pre } = useQuery({
    queryKey: qk.preprintOne(proposal.preprintId ?? "none"),
    queryFn: () => api.get<PreprintView>(`/preprints/${proposal.preprintId}`),
    enabled: !!proposal.preprintId,
  });
  const { data: sel } = useQuery({
    queryKey: qk.selectedOne(pre?.selectedId ?? "none"),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${pre?.selectedId}`),
    enabled: !!pre?.selectedId,
  });
  const aeSubs = sel ? aeSubdisciplines(sel.pitch as Record<string, unknown>) : [];
  const [f, setF] = useState({
    subdiscipline: proposal.subdiscipline ?? "",
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
    bestMatchUrl: proposal.doiBestMatch ?? "",
    affiliation: proposal.affiliation ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    otherDegree: proposal.otherDegree ?? "",
    title: proposal.title ?? "",
    otherTitles: proposal.otherTitles ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    profileJustification: proposal.profileJustification ?? "",
    // Legacy SoftR parity (Angel, 8/20): PubMed defaults to a first+last name search, editable.
    publicationLink:
      proposal.linkToPublications?.trim() ||
      proposal.pubMedLink?.trim() ||
      pubmedSearchUrl(proposal.firstName, proposal.lastName),
  });
  const save = useMutation({
    mutationFn: () => api.post(`/proposals/${proposal.id}/evaluate`, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const sub = f.subdiscipline.trim();
  const paper = f.bestMatchPaperTitle.trim();
  const justificationReady = !!(sub && paper);
  const justificationPreview = justificationReady
    ? deriveJustification(sub, paper)
    : `Because of your expertise in ${sub || "…"}, and especially your paper "${paper || "…"}", we think you'd be a terrific reviewer for this preprint.`;
  const incomplete =
    !sub ||
    !paper ||
    !f.bestMatchUrl.trim() ||
    !f.highestDegree.trim() ||
    !f.title.trim() ||
    !f.linkToProfile.trim() ||
    !f.profileJustification.trim() ||
    !f.publicationLink.trim();

  return (
    <Modal open onClose={onClose} title={`Reviewer profile (Part 2) — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Complete the reviewer profile to finish this proposal — it reaches quality check only once this is submitted.
        </p>
        {/* WO-7/21 Track G: the Part-1 identity travels with the form so the profile reads
            complete before submit (name/email are fixed once COI ran). */}
        <div className="rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
          {proposal.firstName} {proposal.lastName}
          {proposal.reviewerEmail && <span className="ml-2 text-slate-400">{proposal.reviewerEmail}</span>}
        </div>
        <Field label="Affiliation">
          <Input value={f.affiliation} onChange={(e) => setF({ ...f, affiliation: e.target.value })} />
        </Field>
        {/* WO-7/22 Track F: a contested COI travels with the proposal — remind the student the
            AE will read their pushback alongside this profile. */}
        {proposal.coiContestJustification && (
          <div className="rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-800">
            <span className="font-semibold">COI contested.</span> Your justification will be shown
            to the AE with this profile: <span className="italic">{proposal.coiContestJustification}</span>
          </div>
        )}
        {/* Angel (Aug 2026): AE pitch-deck word bank ABOVE the free-text field — pick one or write your own. */}
        <div className="rounded-md border border-blue-100 bg-blue-50/60 px-2.5 py-2">
          <p className="text-xs font-medium text-slate-700">
            AE-suggested expertise phrases
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Pick one from the pitch deck, or type your own below.
          </p>
          {aeSubs.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {aeSubs.map((sub) => (
                <button
                  key={sub}
                  type="button"
                  className={clsx(
                    "rounded-full border px-2.5 py-0.5 text-xs font-medium transition",
                    f.subdiscipline.trim() === sub
                      ? "border-blue-700 bg-blue-700 text-white"
                      : "border-blue-300 bg-white text-blue-800 hover:bg-blue-100",
                  )}
                  aria-pressed={f.subdiscipline.trim() === sub}
                  onClick={() => setF({ ...f, subdiscipline: sub })}
                >
                  {sub}
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-1.5 text-xs italic text-slate-400">
              {sel
                ? "No AE suggestions on this deck yet — write your own below."
                : "Loading AE suggestions…"}
            </p>
          )}
        </div>
        {/* Angel: label is the invitation lead-in, not "Sub-discipline". */}
        <Field label="Because of your expertise in…" required>
          <Input
            value={f.subdiscipline}
            onChange={(e) => setF({ ...f, subdiscipline: e.target.value })}
            placeholder="e.g. computational epidemiology"
            required
          />
        </Field>
        <Field label="Best-match paper URL or DOI" required>
          <Input
            type="url"
            value={f.bestMatchUrl}
            onChange={(e) => setF({ ...f, bestMatchUrl: e.target.value })}
            placeholder="https://doi.org/10.…"
            required
          />
        </Field>
        <Field label="Best-match paper title" required>
          <Input
            value={f.bestMatchPaperTitle}
            onChange={(e) => setF({ ...f, bestMatchPaperTitle: e.target.value })}
            required
          />
        </Field>
        {/* WO-7/22 Track D: the BMP guard-rail note. */}
        <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
          ⚠ {BMP_NOTE}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Secondary affiliation">
            <Input
              value={f.secondaryAffiliation}
              onChange={(e) => setF({ ...f, secondaryAffiliation: e.target.value })}
            />
          </Field>
          <HighestDegreeField
            value={f.highestDegree}
            onChange={(highestDegree) => setF({ ...f, highestDegree })}
            required
          />
        </div>
        <Field label="Other Degrees">
          <Input value={f.otherDegree} onChange={(e) => setF({ ...f, otherDegree: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Title" required>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required />
          </Field>
          <Field label="Other titles">
            <Input value={f.otherTitles} onChange={(e) => setF({ ...f, otherTitles: e.target.value })} />
          </Field>
        </div>
        <Field label="Institutional profile (link)" required>
          <Input
            value={f.linkToProfile}
            onChange={(e) => setF({ ...f, linkToProfile: e.target.value })}
            placeholder="https://… faculty or lab page"
            required
          />
        </Field>
        <Field label="Justification if not institutional profile" required>
          <p className="mb-1 text-xs text-slate-500">
            If you provided the Institutional Profile (link) above, please write N/A in the field.
          </p>
          <Textarea
            value={f.profileJustification}
            onChange={(e) => setF({ ...f, profileJustification: e.target.value })}
            placeholder="N/A — or why this profile page if it is not a university/lab site"
            rows={3}
            required
          />
        </Field>
        <Field label="Publication / PubMed link" required>
          <Input
            value={f.publicationLink}
            onChange={(e) => setF({ ...f, publicationLink: e.target.value })}
            placeholder="https://pubmed.ncbi.nlm.nih.gov/…"
            required
          />
        </Field>

        {/* Invite email sentence — live preview only (FR-2.12 / Hildy nudge). */}
        <div className="rounded-md border border-blue-100 bg-blue-50 p-2.5 text-sm text-blue-950">
          <span className="text-xs font-semibold text-blue-900">Justification preview</span>
          <p className="mt-1 text-xs text-blue-800/80">
            This sentence is copied directly into the reviewer invitation email — read it aloud and fix anything that
            sounds awkward before submitting.
          </p>
          <p className={clsx("mt-1.5 italic", justificationReady ? "text-blue-950" : "text-blue-800/60")}>
            {justificationPreview}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          {/* Aug 19 sitting: "Finish later" removed — it never actually saved fields, and the
              decision was to force propose-asap rather than build autosave. Close via the modal
              chrome if abandoning. */}
          {/* WO-7/22 Track D [20:30–22:00]: the single student action, named for its state. */}
          <Button onClick={() => save.mutate()} disabled={incomplete || save.isPending}>
            {save.isPending ? "Submitting…" : "Submit Reviewer"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
